"use client";

import type { FC } from "react";
import { useCallback } from "react";

import type { BuilderBlock, BuilderContextInterface, RegisteredComponents } from "@builder.io/sdk-react";
import { Blocks, isEditing } from "@builder.io/sdk-react";

import { useMessageBus } from "@jti/fe-message-bus";
import { WelcomePopup as WelcomePopupUI } from "@jti/ui/components/WelcomePopup";

import { useLanguages } from "@/hooks/useLanguages";
import { useDisplayModelComponent } from "@/libs/builder/hooks/useDisplayModelComponent";

import type { welcomePopupInputs } from "./WelcomePopup.builder";

export type WelcomePopupProps = BuilderComponentProps<typeof welcomePopupInputs> & {
  builderBlock: BuilderBlock;
  builderContext: BuilderContextInterface;
  builderComponents: RegisteredComponents;
};

export const WelcomePopup: FC<WelcomePopupProps> = ({
  cta,
  image,
  logo,
  title,
  paragraph,
  legalNotice,
  socialMediaLinks,
  socialMediaText,
  backgroundColor,
  builderBlock,
  builderContext,
  builderComponents,
}) => {
  const languages = useLanguages();
  const shouldDisplayModelComponent = useDisplayModelComponent("welcome-popup");
  const messageBus = useMessageBus();

  const handleOpenChange = useCallback(
    (isOpen: boolean) => {
      messageBus.publish("welcome.popup.state", { payload: { isOpen } }, { sticky: true });
    },
    [messageBus]
  );

  if (!shouldDisplayModelComponent) {
    return null;
  }
  return (
    <WelcomePopupUI
      externalContent={
        <>
          {builderBlock?.children?.map((child) => (
            <Blocks
              key={child.id}
              blocks={[child]}
              parent={builderBlock?.id}
              path="children"
              context={builderContext}
              registeredComponents={builderComponents}
            />
          ))}
        </>
      }
      languages={languages}
      title={title}
      paragraph={paragraph}
      cta={cta}
      logo={logo}
      image={image}
      legalNotice={legalNotice}
      socialMediaLinks={socialMediaLinks}
      backgroundColor={backgroundColor}
      socialMediaText={socialMediaText}
      isAuthorMode={isEditing()}
      onOpenChange={handleOpenChange}
    />
  );
};
